import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiStar, FiCheck, FiShoppingCart } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const PremiumThemes = ({ profile, theme }) => {
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const premiumThemes = [
    {
      id: 'luxe',
      name: 'Luxe',
      description: 'Elegant luxury theme with gold accents',
      price: '0.1 ETH',
      preview: {
        bg: 'bg-gradient-to-br from-amber-50 to-orange-50',
        text: 'text-amber-900',
        accent: 'bg-gradient-to-r from-amber-600 to-orange-600',
        border: 'border-amber-200',
      },
      features: ['Premium fonts', 'Gold animations', 'Custom layouts', 'Priority support']
    },
    {
      id: 'pastel-creator',
      name: 'Pastel Creator',
      description: 'Soft pastel colors for creators',
      price: '0.05 ETH',
      preview: {
        bg: 'bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-50',
        text: 'text-purple-900',
        accent: 'bg-gradient-to-r from-pink-400 to-purple-400',
        border: 'border-purple-200',
      },
      features: ['Pastel palette', 'Creator tools', 'Social integration', 'Analytics boost']
    },
    {
      id: 'brutalist',
      name: 'Brutalist',
      description: 'Bold and unconventional design',
      price: '0.08 ETH',
      preview: {
        bg: 'bg-gray-900',
        text: 'text-white',
        accent: 'bg-gradient-to-r from-red-600 to-yellow-600',
        border: 'border-gray-700',
      },
      features: ['Bold typography', 'Unique layouts', 'High contrast', 'Stand out']
    },
    {
      id: 'techno-lines',
      name: 'Techno Lines',
      description: 'Futuristic tech-inspired theme',
      price: '0.06 ETH',
      preview: {
        bg: 'bg-gradient-to-br from-cyan-900 via-blue-900 to-purple-900',
        text: 'text-cyan-400',
        accent: 'bg-gradient-to-r from-cyan-400 to-purple-400',
        border: 'border-cyan-700',
      },
      features: ['Tech animations', 'Circuit patterns', 'Neon effects', 'Futuristic']
    },
    {
      id: 'gradient-aurora',
      name: 'Gradient Aurora',
      description: 'Beautiful gradient animations',
      price: '0.12 ETH',
      preview: {
        bg: 'bg-gradient-to-br from-purple-600 via-pink-600 to-blue-600',
        text: 'text-white',
        accent: 'bg-white',
        border: 'border-white/20',
      },
      features: ['Animated gradients', 'Smooth transitions', 'Premium effects', 'Eye-catching']
    }
  ];

  const handlePurchase = (themeData) => {
    setSelectedTheme(themeData);
    setShowPurchaseModal(true);
  };

  const ThemePreview = ({ themeData }) => (
    <div className={`p-4 rounded-lg border-2 ${themeData.preview.border} ${themeData.preview.bg} relative overflow-hidden`}>
      {/* Animated background effect for premium themes */}
      {themeData.id === 'gradient-aurora' && (
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 opacity-30 animate-pulse"></div>
      )}
      <div className="relative z-10">
        {/* Mock profile header */}
        <div className="flex items-center space-x-3 mb-4">
          <div className={`w-12 h-12 ${themeData.preview.accent} rounded-full`}></div>
          <div>
            <div className={`h-3 w-20 ${themeData.preview.text} bg-current opacity-80 rounded mb-1`}></div>
            <div className={`h-2 w-16 ${themeData.preview.text} bg-current opacity-50 rounded`}></div>
          </div>
        </div>
        {/* Mock content blocks */}
        <div className="space-y-2">
          <div className={`h-8 ${themeData.preview.border} border rounded`}></div>
          <div className={`h-6 ${themeData.preview.border} border rounded`}></div>
          <div className={`h-10 ${themeData.preview.accent} rounded`}></div>
        </div>
      </div>
      {/* Premium badge */}
      <div className="absolute top-2 right-2 w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full flex items-center justify-center">
        <SafeIcon icon={FiStar} className="text-white text-xs" />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Premium Themes</h3>
        <p className="text-gray-600">
          Unlock exclusive themes to make your profile stand out
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {premiumThemes.map((themeData) => (
          <motion.div
            key={themeData.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className={`relative rounded-xl border-2 ${themeData.preview.border} overflow-hidden hover:shadow-xl transition-shadow`}
          >
            {/* Premium banner */}
            <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-xs font-medium py-1 text-center z-20">
              PREMIUM
            </div>
            {/* Theme preview */}
            <div className="pt-6">
              <ThemePreview themeData={themeData} />
            </div>
            {/* Theme info */}
            <div className="p-4 bg-white">
              <h4 className="font-semibold text-gray-900 mb-2">{themeData.name}</h4>
              <p className="text-sm text-gray-600 mb-3">{themeData.description}</p>
              {/* Features */}
              <div className="space-y-1 mb-4">
                {themeData.features.slice(0, 3).map((feature, index) => (
                  <div key={index} className="flex items-center text-xs text-gray-600">
                    <SafeIcon icon={FiCheck} className="text-green-500 mr-2 flex-shrink-0" />
                    {feature}
                  </div>
                ))}
              </div>
              {/* Purchase button */}
              <button
                onClick={() => handlePurchase(themeData)}
                className="w-full inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 transition-all transform hover:scale-105"
              >
                <SafeIcon icon={FiShoppingCart} className="mr-2" />
                {themeData.price}
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Purchase Modal */}
      {showPurchaseModal && selectedTheme && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 max-w-md w-full"
          >
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Purchase {selectedTheme.name} Theme
            </h3>
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${selectedTheme.preview.bg} ${selectedTheme.preview.border} border`}>
                <ThemePreview themeData={selectedTheme} />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Theme Price:</span>
                  <span className="font-medium">{selectedTheme.price}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Gas Estimation:</span>
                  <span className="font-medium">~0.001 ETH</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold">
                  <span>Total:</span>
                  <span>{selectedTheme.price}</span>
                </div>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowPurchaseModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // Handle purchase logic here
                    alert(`Purchasing ${selectedTheme.name} for ${selectedTheme.price}`);
                    setShowPurchaseModal(false);
                  }}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Complete Purchase
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PremiumThemes;